// src/components/SupervisorBar.js
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
    AppBar,
    Avatar,
    Badge,
    Box,
    Button,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Toolbar,
    Typography,
    useMediaQuery,
    ClickAwayListener,
    Grow,
    Popper,
    MenuList,
    Fade,
    Chip,
    Stack,
    Skeleton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard,
    Topic,
    History,
    CompareArrows,
    PersonSearch,
    Timeline,
    Feedback,
    FolderOpen,
    UploadFile,
    Article,
    FileCopy,
    Plagiarism,
    Person,
    Message,
    Groups,
    VerifiedUser,
    Paid,
    School,
    Settings,
    Logout,
    ChevronRight,
    KeyboardArrowDown,
    Home,
    Science,
    Assessment,
    Description,
    Assignment,
    Notifications,
    NotificationsActive,
    NotificationsNone,
    SupervisorAccount,
    RateReview,
    Analytics,
    TrendingUp,
    Email,
    Phone,
    Flag,
    Badge as BadgeIcon,
    Work,
    Chat,
    Close as CloseIcon,
    Info,
    Warning,
    CheckCircle,
    Error,
    Delete,
    DoneAll,
    OpenInNew
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import { formatDate, formatDateTime, getImageUrl, getUserDetails } from '../utils/helpers';
import authService from '../apiServices/authService';
import { 
    APISupervisorProfileImage, 
    APISupervisorProfileById,
    APISupervisorNotificationsRecent,
    APISupervisorNotificationsMarkRead,
    APISupervisorNotificationsMarkAllRead,
    APISupervisorNotificationsDelete
} from '../apiServices/APIs';
import { notificationService } from '../apiServices/notificationService';
import { apiService } from '../apiServices/ApiService';

const SupervisorBar = ({ notificationCount = 0 }) => {
    const theme = useTheme();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
    const [userData, setUserData] = useState({
        name: '',
        email: '',
        contact_phone: '',
        nationality: '',
        profile_image_path: null,
        university_name: '',
        university_id: null,
        academic_title: '',
        role: 'Supervisor',
        id: null
    });
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
    
    // Store anchor elements for each dropdown separately
    const [overviewAnchorEl, setOverviewAnchorEl] = useState(null);
    const [managementAnchorEl, setManagementAnchorEl] = useState(null);
    const [analyticsAnchorEl, setAnalyticsAnchorEl] = useState(null);

    const openMenu = Boolean(anchorEl);
    const openNotificationMenu = Boolean(notificationAnchorEl);
    const openOverview = Boolean(overviewAnchorEl);
    const openManagement = Boolean(managementAnchorEl);
    const openAnalytics = Boolean(analyticsAnchorEl);

    // Fetch recent notifications using notificationService
    const fetchRecentNotifications = useCallback(async () => {
        try {
            const userId = getUserDetails("Id");
            if (!userId) return;
            
            setNotificationsLoading(true);
            const response = await notificationService.getRecent(
                APISupervisorNotificationsRecent,
                { supervisor_id: userId, limit: 6 }
            );
            
            if (response.success) {
                setNotifications(response.data || []);
                setUnreadCount(response.unread_count || 0);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setNotificationsLoading(false);
        }
    }, []);

    // Fetch user data from API
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const userId = getUserDetails("Id");
                if (userId) {
                    const response = await apiService.show(APISupervisorProfileById, userId);
                    if (response.success) {
                        const data = response.data;
                        setUserData({
                            name: data.full_name_with_title || data.full_name || getUserDetails("userName") || 'Supervisor',
                            email: data.email || getUserDetails("email") || '',
                            contact_phone: data.contact_phone || '',
                            nationality: data.nationality || '',
                            profile_image_path: data.profile_image_path || null,
                            university_name: data.university_name || '',
                            university_id: data.university_id || null,
                            academic_title: data.formatted_academic_title || data.academic_title || '',
                            role: 'Supervisor',
                            id: data.id || userId
                        });
                    } else {
                        // Fallback to sessionStorage data
                        setUserData({
                            name: getUserDetails("userName") || 'Supervisor',
                            email: getUserDetails("email") || '',
                            contact_phone: getUserDetails("contact") || '',
                            nationality: getUserDetails("nationality") || '',
                            profile_image_path: getUserDetails("profileImage") || null,
                            university_name: '',
                            university_id: null,
                            academic_title: '',
                            role: 'Supervisor',
                            id: userId
                        });
                    }
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                // Fallback to sessionStorage data
                setUserData({
                    name: getUserDetails("userName") || 'Supervisor',
                    email: getUserDetails("email") || '',
                    contact_phone: getUserDetails("contact") || '',
                    nationality: getUserDetails("nationality") || '',
                    profile_image_path: getUserDetails("profileImage") || null,
                    university_name: '',
                    university_id: null,
                    academic_title: '',
                    role: 'Supervisor',
                    id: getUserDetails("Id")
                });
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
        fetchRecentNotifications();
        
        // Set up interval to fetch notifications every 30 seconds
        const interval = setInterval(fetchRecentNotifications, 30000);
        
        return () => clearInterval(interval);
    }, [fetchRecentNotifications]);

    // Mark notification as read using notificationService
    const markAsRead = async (notificationId) => {
        try {
            const response = await notificationService.markAsRead(
                APISupervisorNotificationsMarkRead(notificationId),
                {}
            );
            
            if (response.success) {
                setNotifications(prev => 
                    prev.map(notif => 
                        notif.id === notificationId 
                            ? { ...notif, is_read: true, read_at: new Date().toISOString() }
                            : notif
                    )
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    // Mark all as read using notificationService
    const markAllAsRead = async () => {
        try {
            const userId = getUserDetails("Id") || userData.id;
            if (!userId) return;

            const response = await notificationService.markAllAsRead(
                APISupervisorNotificationsMarkAllRead,
                { supervisor_id: userId }
            );
            
            if (response.success) {
                setNotifications(prev => 
                    prev.map(notif => ({ ...notif, is_read: true, read_at: new Date().toISOString() }))
                );
                setUnreadCount(0);
            }
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    // Delete notification using notificationService
    const deleteNotification = async (notificationId) => {
        try {
            const response = await notificationService.delete(
                APISupervisorNotificationsDelete(notificationId)
            );
            
            if (response.success) {
                setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
                if (selectedNotification?.id === notificationId) {
                    setNotificationDialogOpen(false);
                    setSelectedNotification(null);
                }
                // Update unread count
                const newUnreadCount = notifications.filter(n => n.id !== notificationId && !n.is_read).length;
                setUnreadCount(newUnreadCount);
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    // Handle notification click
    const handleNotificationClick = async (notification) => {
        setSelectedNotification(notification);
        setNotificationDialogOpen(true);
        
        if (!notification.is_read) {
            await markAsRead(notification.id);
        }
    };

    // Handle action URL click
    const handleActionUrlClick = (url) => {
        if (url) {
            window.open(url, '_blank');
        }
    };

    // Get notification icon based on type
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'success':
                return <CheckCircle sx={{ color: '#4caf50' }} />;
            case 'warning':
                return <Warning sx={{ color: '#ff9800' }} />;
            case 'error':
                return <Error sx={{ color: '#f44336' }} />;
            default:
                return <Info sx={{ color: '#2196f3' }} />;
        }
    };

    // Navigation links organized by categories for dropdown
    const navigationLinks = useMemo(
        () => [
            {
                category: 'Overview',
                icon: <Dashboard />,
                paths: ['/supervisor/dashboard'],
                links: [
                    { title: 'Dashboard', path: '/supervisor/dashboard', icon: <Dashboard />, description: 'Overview of your supervision activities' },
                ]
            },
            {
                category: 'Management',
                icon: <SupervisorAccount />,
                paths: [
                    '/supervisor/topic-view',
                    '/supervisor/works',
                    '/supervisor/assigned-student',
                    '/main/topics-upladed',
                    '/main/projects-upload',
                ],
                links: [
                    { title: 'Research Topic', path: '/supervisor/topic-view', icon: <Groups />, description: 'View research topics' },
                    { title: 'Research Project', path: '/supervisor/works', icon: <Groups />, description: 'View research project' },
                    { title: 'Assigned Students', path: '/supervisor/assigned-student', icon: <Groups />, description: 'View your assigned students' },
                    { title: 'Uploaded Topics', path: '/main/topics-upladed', icon: <Topic />, description: 'All Uploaded research topics' },
                    { title: 'Uploaded Projects', path: '/main/projects-upload', icon: <UploadFile />, description: 'All Upload research projects' },
                ]
            },
            {
                category: 'Analytics',
                icon: <Analytics />,
                paths: [
                    '/supervisor/speciality',
                ],
                links: [
                    { title: 'My Specialities', path: '/supervisor/speciality', icon: <CompareArrows />, description: 'Your Specialities' },
                    { title: 'My Institutions', path: '/supervisor/institution', icon: <CompareArrows />, description: 'Your Institutions' },
                ]
            }
        ],
        []
    );

    // Check if a category is active (any of its paths match current location)
    const isCategoryActive = (categoryPaths) => {
        return categoryPaths.some(path => location.pathname === path);
    };

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };
    
    // Handle dropdown open/close
    const handleOverviewOpen = (event) => {
        setOverviewAnchorEl(event.currentTarget);
    };

    const handleOverviewClose = () => {
        setOverviewAnchorEl(null);
    };

    const handleManagementOpen = (event) => {
        setManagementAnchorEl(event.currentTarget);
    };

    const handleManagementClose = () => {
        setManagementAnchorEl(null);
    };

    const handleAnalyticsOpen = (event) => {
        setAnalyticsAnchorEl(event.currentTarget);
    };

    const handleAnalyticsClose = () => {
        setAnalyticsAnchorEl(null);
    };

    const handleNotificationOpen = (event) => {
        setNotificationAnchorEl(event.currentTarget);
        fetchRecentNotifications();
    };

    const handleNotificationClose = () => {
        setNotificationAnchorEl(null);
    };

    // Get profile image URL
    const profileImageUrl = userData.profile_image_path 
        ? getImageUrl(APISupervisorProfileImage, userData.profile_image_path)
        : null;

    // Get initials for avatar
    const getInitials = (name) => {
        if (!name) return 'SU';
        return name
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    // Notification Dialog Component
    const NotificationDialog = () => (
        <Dialog
            open={notificationDialogOpen}
            onClose={() => {
                setNotificationDialogOpen(false);
                setSelectedNotification(null);
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden'
                }
            }}
        >
            {selectedNotification && (
                <>
                    <DialogTitle sx={{ 
                        p: 2.5, 
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {getNotificationIcon(selectedNotification.type)}
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {selectedNotification.title}
                            </Typography>
                        </Box>
                        <IconButton 
                            onClick={() => {
                                setNotificationDialogOpen(false);
                                setSelectedNotification(null);
                            }}
                            sx={{ color: 'white' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    
                    <DialogContent sx={{ p: 3 }}>
                        {/* Metadata Section */}
                        <Stack spacing={2} sx={{ mb: 3 }}>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2,
                                p: 1.5,
                                bgcolor: theme.palette.grey[50],
                                borderRadius: 2,
                                flexWrap: 'wrap'
                            }}>
                                {selectedNotification.university_name && (
                                    <Chip
                                        label={selectedNotification.university_name}
                                        size="small"
                                        sx={{
                                            bgcolor: theme.palette.primary.main,
                                            color: 'white',
                                            fontWeight: 600
                                        }}
                                    />
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                    {formatDateTime(selectedNotification.created_at)}
                                </Typography>
                            </Box>
                        </Stack>

                        {/* Message Content */}
                        <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
                            {selectedNotification.message}
                        </Typography>

                        {/* Action URL Button */}
                        {selectedNotification.action_url && (
                            <Button
                                variant="outlined"
                                startIcon={<OpenInNew />}
                                onClick={() => handleActionUrlClick(selectedNotification.action_url)}
                                fullWidth
                                sx={{ mb: 2 }}
                            >
                                View Related Item
                            </Button>
                        )}

                        {/* Metadata JSON Display */}
                        {/* {selectedNotification.metadata && (
                            <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: theme.palette.grey[50] }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    Additional Information
                                </Typography>
                                <pre style={{ 
                                    margin: 0, 
                                    fontSize: '0.75rem',
                                    fontFamily: 'monospace',
                                    overflow: 'auto'
                                }}>
                                    {JSON.stringify(selectedNotification.metadata, null, 2)}
                                </pre>
                            </Paper>
                        )} */}
                    </DialogContent>
                    
                    <DialogActions sx={{ p: 2.5, pt: 0, justifyContent: 'space-between' }}>
                        <Button
                            onClick={() => deleteNotification(selectedNotification.id)}
                            color="error"
                            startIcon={<Delete />}
                            variant="outlined"
                        >
                            Delete
                        </Button>
                        <Button
                            onClick={() => {
                                setNotificationDialogOpen(false);
                                setSelectedNotification(null);
                            }}
                            variant="contained"
                        >
                            Close
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );

    const drawerContent = (
        <Box
            sx={{
                width: 280,
                height: '100%',
                background: `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                    src={profileImageUrl}
                    sx={{
                        width: 48,
                        height: 48,
                        bgcolor: !profileImageUrl ? 'white' : 'transparent',
                        color: theme.palette.primary.main,
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                    }}
                >
                    {!profileImageUrl && getInitials(userData.name)}
                </Avatar>

                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {userData.name}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Supervisor Portal
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />

            <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 2 }}>
                {navigationLinks.map((category, catIndex) => {
                    const isActive = isCategoryActive(category.paths);
                    
                    return (
                        <Box key={catIndex}>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    px: 2, 
                                    py: 1, 
                                    display: 'block',
                                    opacity: 0.7,
                                    fontWeight: 600,
                                    letterSpacing: '0.5px',
                                    color: isActive ? theme.palette.warning.light : 'rgba(255,255,255,0.7)'
                                }}
                            >
                                {category.category}
                            </Typography>
                            <List sx={{ py: 0, mb: 2 }}>
                                {category.links.map((item, index) => {
                                    const active = location.pathname === item.path;

                                    return (
                                        <ListItemButton
                                            key={index}
                                            component={Link}
                                            to={item.path}
                                            onClick={() => setMobileOpen(false)}
                                            sx={{
                                                borderRadius: 2,
                                                mb: 0.5,
                                                py: 1,
                                                backgroundColor: active ? 'rgba(255,255,255,0.15)' : 'transparent',
                                                backdropFilter: active ? 'blur(6px)' : 'none',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.12)',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.title}
                                                primaryTypographyProps={{
                                                    fontSize: '0.9rem',
                                                    fontWeight: active ? 700 : 500,
                                                }}
                                            />
                                            {active && <ChevronRight sx={{ opacity: 0.7, fontSize: 18 }} />}
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </Box>
                    );
                })}
            </Box>

            <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleLogout}
                    startIcon={<Logout />}
                    sx={{
                        color: 'white',
                        borderColor: 'rgba(255,255,255,0.5)',
                        '&:hover': {
                            borderColor: 'white',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                        },
                        textTransform: 'none',
                    }}
                >
                    Logout
                </Button>
            </Box>
        </Box>
    );

    // Overview Dropdown Menu
    const OverviewDropdownMenu = () => (
        <Popper
            open={openOverview}
            anchorEl={overviewAnchorEl}
            placement="bottom-start"
            transition
            sx={{ zIndex: 1300 }}
        >
            {({ TransitionProps }) => (
                <Grow {...TransitionProps} timeout={200}>
                    <Paper
                        sx={{
                            mt: 1,
                            minWidth: 260,
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            overflow: 'hidden',
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <ClickAwayListener onClickAway={handleOverviewClose}>
                            <MenuList>
                                {navigationLinks.find(cat => cat.category === 'Overview')?.links.map((item, index) => {
                                    const active = location.pathname === item.path;
                                    return (
                                        <MenuItem
                                            key={index}
                                            component={Link}
                                            to={item.path}
                                            onClick={handleOverviewClose}
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                backgroundColor: active ? theme.palette.action.hover : 'transparent',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.main,
                                                    '& .MuiListItemIcon-root': {
                                                        color: 'white',
                                                    },
                                                    '& .MuiListItemText-primary': {
                                                        color: 'white',
                                                    },
                                                    '& .MuiTypography-caption': {
                                                        color: 'rgba(255,255,255,0.8)',
                                                    },
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ 
                                                minWidth: 36, 
                                                color: active ? theme.palette.primary.main : theme.palette.primary.dark,
                                                transition: 'color 0.2s ease'
                                            }}>
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={item.title}
                                                secondary={item.description}
                                                primaryTypographyProps={{
                                                    fontWeight: active ? 600 : 400,
                                                    fontSize: '0.9rem',
                                                }}
                                                secondaryTypographyProps={{
                                                    fontSize: '0.75rem',
                                                    noWrap: true,
                                                }}
                                            />
                                            {active && <ChevronRight sx={{ fontSize: 18, color: theme.palette.primary.main }} />}
                                        </MenuItem>
                                    );
                                })}
                            </MenuList>
                        </ClickAwayListener>
                    </Paper>
                </Grow>
            )}
        </Popper>
    );

    // Management Dropdown Menu
    const ManagementDropdownMenu = () => (
        <Popper
            open={openManagement}
            anchorEl={managementAnchorEl}
            placement="bottom-start"
            transition
            sx={{ zIndex: 1300 }}
        >
            {({ TransitionProps }) => (
                <Grow {...TransitionProps} timeout={200}>
                    <Paper
                        sx={{
                            mt: 1,
                            minWidth: 260,
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            overflow: 'hidden',
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <ClickAwayListener onClickAway={handleManagementClose}>
                            <MenuList>
                                {navigationLinks.find(cat => cat.category === 'Management')?.links.map((item, index) => {
                                    const active = location.pathname === item.path;
                                    return (
                                        <MenuItem
                                            key={index}
                                            component={Link}
                                            to={item.path}
                                            onClick={handleManagementClose}
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                backgroundColor: active ? theme.palette.action.hover : 'transparent',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.main,
                                                    '& .MuiListItemIcon-root': {
                                                        color: 'white',
                                                    },
                                                    '& .MuiListItemText-primary': {
                                                        color: 'white',
                                                    },
                                                    '& .MuiTypography-caption': {
                                                        color: 'rgba(255,255,255,0.8)',
                                                    },
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ 
                                                minWidth: 36, 
                                                color: active ? theme.palette.primary.main : theme.palette.primary.dark,
                                                transition: 'color 0.2s ease'
                                            }}>
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={item.title}
                                                secondary={item.description}
                                                primaryTypographyProps={{
                                                    fontWeight: active ? 600 : 400,
                                                    fontSize: '0.9rem',
                                                }}
                                                secondaryTypographyProps={{
                                                    fontSize: '0.75rem',
                                                    noWrap: true,
                                                }}
                                            />
                                            {active && <ChevronRight sx={{ fontSize: 18, color: theme.palette.primary.main }} />}
                                        </MenuItem>
                                    );
                                })}
                            </MenuList>
                        </ClickAwayListener>
                    </Paper>
                </Grow>
            )}
        </Popper>
    );

    // Analytics Dropdown Menu
    const AnalyticsDropdownMenu = () => (
        <Popper
            open={openAnalytics}
            anchorEl={analyticsAnchorEl}
            placement="bottom-start"
            transition
            sx={{ zIndex: 1300 }}
        >
            {({ TransitionProps }) => (
                <Grow {...TransitionProps} timeout={200}>
                    <Paper
                        sx={{
                            mt: 1,
                            minWidth: 260,
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            overflow: 'hidden',
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <ClickAwayListener onClickAway={handleAnalyticsClose}>
                            <MenuList>
                                {navigationLinks.find(cat => cat.category === 'Analytics')?.links.map((item, index) => {
                                    const active = location.pathname === item.path;
                                    return (
                                        <MenuItem
                                            key={index}
                                            component={Link}
                                            to={item.path}
                                            onClick={handleAnalyticsClose}
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                backgroundColor: active ? theme.palette.action.hover : 'transparent',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.main,
                                                    '& .MuiListItemIcon-root': {
                                                        color: 'white',
                                                    },
                                                    '& .MuiListItemText-primary': {
                                                        color: 'white',
                                                    },
                                                    '& .MuiTypography-caption': {
                                                        color: 'rgba(255,255,255,0.8)',
                                                    },
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ 
                                                minWidth: 36, 
                                                color: active ? theme.palette.primary.main : theme.palette.primary.dark,
                                                transition: 'color 0.2s ease'
                                            }}>
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={item.title}
                                                secondary={item.description}
                                                primaryTypographyProps={{
                                                    fontWeight: active ? 600 : 400,
                                                    fontSize: '0.9rem',
                                                }}
                                                secondaryTypographyProps={{
                                                    fontSize: '0.75rem',
                                                    noWrap: true,
                                                }}
                                            />
                                            {active && <ChevronRight sx={{ fontSize: 18, color: theme.palette.primary.main }} />}
                                        </MenuItem>
                                    );
                                })}
                            </MenuList>
                        </ClickAwayListener>
                    </Paper>
                </Grow>
            )}
        </Popper>
    );

    return (
        <>
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    background: 'white',
                    color: theme.palette.text.primary,
                }}
            >
                <Toolbar sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    gap: 1,
                    px: { xs: 1, sm: 2, md: 3 },
                    minHeight: { xs: '64px', sm: '70px' }
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            onClick={() => setMobileOpen(true)}
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                color: theme.palette.primary.main,
                            }}
                        >
                            <MenuIcon />
                        </IconButton>

                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 800,
                                color: theme.palette.primary.main,
                                letterSpacing: 0.5,
                                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
                            }}
                        >
                            Supervisor Research Portal
                        </Typography>
                    </Box>

                    {/* Desktop Navigation with Dropdowns - Rounded Category Buttons */}
                    {!isMobile && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: { md: 0.5, lg: 1 },
                                flex: 1,
                                justifyContent: 'center',
                            }}
                        >
                            {/* Overview Category Button */}
                            <Button
                                onClick={handleOverviewOpen}
                                endIcon={<KeyboardArrowDown />}
                                startIcon={<Dashboard />}
                                sx={{
                                    borderRadius: '40px',
                                    px: { md: 2, lg: 2.5 },
                                    py: { md: 0.8, lg: 1 },
                                    whiteSpace: 'nowrap',
                                    textTransform: 'none',
                                    fontSize: { md: '0.75rem', lg: '0.875rem' },
                                    fontWeight: 500,
                                    color: isCategoryActive(navigationLinks[0].paths) 
                                        ? theme.palette.primary.main 
                                        : (openOverview ? theme.palette.primary.main : theme.palette.primary.dark),
                                    backgroundColor: isCategoryActive(navigationLinks[0].paths) 
                                        ? `${theme.palette.primary.main}10`
                                        : (openOverview ? theme.palette.action.hover : 'transparent'),
                                    border: isCategoryActive(navigationLinks[0].paths) 
                                        ? `1px solid ${theme.palette.primary.main}`
                                        : '1px solid transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.main,
                                        color: 'white',
                                        transform: 'translateY(-2px)',
                                        borderColor: theme.palette.primary.main,
                                        boxShadow: theme.shadows[4],
                                        '& .MuiButton-startIcon': {
                                            color: 'white',
                                        },
                                        '& .MuiButton-endIcon': {
                                            color: 'white',
                                        },
                                    },
                                }}
                            >
                                Overview
                            </Button>
                            <OverviewDropdownMenu />

                            {/* Management Category Button */}
                            <Button
                                onClick={handleManagementOpen}
                                endIcon={<KeyboardArrowDown />}
                                startIcon={<SupervisorAccount />}
                                sx={{
                                    borderRadius: '40px',
                                    px: { md: 2, lg: 2.5 },
                                    py: { md: 0.8, lg: 1 },
                                    whiteSpace: 'nowrap',
                                    textTransform: 'none',
                                    fontSize: { md: '0.75rem', lg: '0.875rem' },
                                    fontWeight: 500,
                                    color: isCategoryActive(navigationLinks[1].paths) 
                                        ? theme.palette.primary.main 
                                        : (openManagement ? theme.palette.primary.main : theme.palette.primary.dark),
                                    backgroundColor: isCategoryActive(navigationLinks[1].paths) 
                                        ? `${theme.palette.primary.main}10`
                                        : (openManagement ? theme.palette.action.hover : 'transparent'),
                                    border: isCategoryActive(navigationLinks[1].paths) 
                                        ? `1px solid ${theme.palette.primary.main}`
                                        : '1px solid transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.main,
                                        color: 'white',
                                        transform: 'translateY(-2px)',
                                        borderColor: theme.palette.primary.main,
                                        boxShadow: theme.shadows[4],
                                        '& .MuiButton-startIcon': {
                                            color: 'white',
                                        },
                                        '& .MuiButton-endIcon': {
                                            color: 'white',
                                        },
                                    },
                                }}
                            >
                                Management
                            </Button>
                            <ManagementDropdownMenu />

                            {/* Analytics Category Button */}
                            <Button
                                onClick={handleAnalyticsOpen}
                                endIcon={<KeyboardArrowDown />}
                                startIcon={<Analytics />}
                                sx={{
                                    borderRadius: '40px',
                                    px: { md: 2, lg: 2.5 },
                                    py: { md: 0.8, lg: 1 },
                                    whiteSpace: 'nowrap',
                                    textTransform: 'none',
                                    fontSize: { md: '0.75rem', lg: '0.875rem' },
                                    fontWeight: 500,
                                    color: isCategoryActive(navigationLinks[2].paths) 
                                        ? theme.palette.primary.main 
                                        : (openAnalytics ? theme.palette.primary.main : theme.palette.primary.dark),
                                    backgroundColor: isCategoryActive(navigationLinks[2].paths) 
                                        ? `${theme.palette.primary.main}10`
                                        : (openAnalytics ? theme.palette.action.hover : 'transparent'),
                                    border: isCategoryActive(navigationLinks[2].paths) 
                                        ? `1px solid ${theme.palette.primary.main}`
                                        : '1px solid transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.main,
                                        color: 'white',
                                        transform: 'translateY(-2px)',
                                        borderColor: theme.palette.primary.main,
                                        boxShadow: theme.shadows[4],
                                        '& .MuiButton-startIcon': {
                                            color: 'white',
                                        },
                                        '& .MuiButton-endIcon': {
                                            color: 'white',
                                        },
                                    },
                                }}
                            >
                                Analytics
                            </Button>
                            <AnalyticsDropdownMenu />
                        </Box>
                    )}

                    {/* Right side - Notification and Profile */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Chat Icon Button */}
                        {/* <Tooltip title="Messages">
                            <IconButton
                                component={Link}
                                to=""
                                sx={{
                                    transition: 'all 0.2s ease',
                                    position: 'relative',
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                        transform: 'scale(1.05)',
                                    },
                                }}
                            >
                                <Badge 
                                    badgeContent={0} 
                                    color="error"
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            fontSize: '0.7rem',
                                            height: '18px',
                                            minWidth: '18px',
                                        }
                                    }}
                                >
                                    <Chat sx={{ color: theme.palette.primary.dark }} />
                                </Badge>
                            </IconButton>
                        </Tooltip> */}

                        {/* Notification Bell */}
                        <IconButton
                            onClick={handleNotificationOpen}
                            sx={{
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                    transform: 'scale(1.05)',
                                },
                            }}
                        >
                            <Badge 
                                badgeContent={unreadCount}
                                color="error"
                                sx={{
                                    '& .MuiBadge-badge': {
                                        fontSize: '0.7rem',
                                        height: '18px',
                                        minWidth: '18px',
                                    }
                                }}
                            >
                                <NotificationsNone sx={{ color: theme.palette.primary.dark }} />
                            </Badge>
                        </IconButton>

                        {/* Notification Menu */}
                        <Menu
                            anchorEl={notificationAnchorEl}
                            open={openNotificationMenu}
                            onClose={handleNotificationClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            PaperProps={{
                                sx: {
                                    mt: 1,
                                    borderRadius: 2,
                                    minWidth: 380,
                                    maxWidth: 420,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    overflow: 'hidden',
                                },
                            }}
                            TransitionComponent={Fade}
                            TransitionProps={{ timeout: 200 }}
                        >
                            <Box sx={{ 
                                p: 2, 
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                bgcolor: theme.palette.primary.main,
                                color: 'white'
                            }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Notifications
                                </Typography>
                                {unreadCount > 0 && (
                                    <Button
                                        size="small"
                                        onClick={markAllAsRead}
                                        startIcon={<DoneAll />}
                                        sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                                    >
                                        Mark all read
                                    </Button>
                                )}
                            </Box>
                            
                            {notificationsLoading ? (
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <CircularProgress size={40} />
                                </Box>
                            ) : notifications.length === 0 ? (
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <NotificationsNone sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        No new notifications
                                    </Typography>
                                </Box>
                            ) : (
                                <MenuList sx={{ py: 0, maxHeight: 400, overflowY: 'auto' }}>
                                    {notifications.map((notification) => (
                                        <MenuItem
                                            key={notification.id}
                                            onClick={() => handleNotificationClick(notification)}
                                            sx={{
                                                py: 2,
                                                px: 2,
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                backgroundColor: !notification.is_read ? theme.palette.action.hover : 'transparent',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.action.selected,
                                                },
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                gap: 1
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                                                {getNotificationIcon(notification.type)}
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography 
                                                        variant="subtitle2" 
                                                        sx={{ 
                                                            fontWeight: !notification.is_read ? 700 : 600,
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        {notification.title}
                                                    </Typography>
                                                    <Typography 
                                                        variant="caption" 
                                                        color="text.secondary"
                                                        sx={{ display: 'block', mb: 0.5 }}
                                                    >
                                                        {notification.message.length > 80 
                                                            ? `${notification.message.substring(0, 80)}...` 
                                                            : notification.message}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                                        {notification.university_name && (
                                                            <Chip
                                                                label={notification.university_name}
                                                                size="small"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: '0.65rem',
                                                                    bgcolor: theme.palette.primary.main,
                                                                    color: 'white'
                                                                }}
                                                            />
                                                        )}
                                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                                            {formatDate(notification.created_at)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                {!notification.is_read && (
                                                    <Box sx={{ 
                                                        width: 8, 
                                                        height: 8, 
                                                        borderRadius: '50%', 
                                                        bgcolor: theme.palette.primary.main,
                                                        mt: 1
                                                    }} />
                                                )}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            )}
                            
                            <Box sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                                <Button 
                                    size="small" 
                                    component={Link} 
                                    to="/supervisor/notifications"
                                    onClick={handleNotificationClose}
                                    sx={{ textTransform: 'none' }}
                                >
                                    View all notifications
                                </Button>
                            </Box>
                        </Menu>

                        {/* Profile Paper - Enhanced with user info */}
                        <Paper
                            elevation={0}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 0.5, sm: 1.5 },
                                px: { xs: 0.5, sm: 1.5 },
                                py: { xs: 0.5, sm: 1 },
                                borderRadius: '40px',
                                border: `1px solid ${theme.palette.divider}`,
                                backgroundColor: '#fafafa',
                                flexShrink: 0,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                    borderColor: theme.palette.primary.main,
                                    transform: 'translateY(-1px)',
                                    boxShadow: theme.shadows[2],
                                },
                            }}
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                        >
                            <Avatar
                                src={profileImageUrl}
                                sx={{
                                    bgcolor: !profileImageUrl ? theme.palette.primary.main : 'transparent',
                                    width: { xs: 32, sm: 40 },
                                    height: { xs: 32, sm: 40 },
                                    fontWeight: 'bold',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                }}
                            >
                                {!profileImageUrl && getInitials(userData.name)}
                            </Avatar>

                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { sm: '0.75rem', md: '0.875rem' } }}>
                                    {userData.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { sm: '0.65rem', md: '0.75rem' } }}>
                                    {userData.role}
                                </Typography>
                            </Box>

                            <KeyboardArrowDown sx={{ fontSize: 20, color: theme.palette.primary.dark }} />
                        </Paper>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Profile Menu - Enhanced with user details */}
            <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        borderRadius: 2,
                        minWidth: 280,
                        maxWidth: 320,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        overflow: 'hidden',
                    },
                }}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 200 }}
            >
                {/* User Header Section */}
                <Box sx={{ 
                    p: 2, 
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'white'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            src={profileImageUrl}
                            sx={{
                                width: 56,
                                height: 56,
                                bgcolor: !profileImageUrl ? 'white' : 'transparent',
                                color: theme.palette.primary.main,
                                fontWeight: 'bold',
                                fontSize: '1.2rem',
                                border: '2px solid white',
                            }}
                        >
                            {!profileImageUrl && getInitials(userData.name)}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {userData.name}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <BadgeIcon sx={{ fontSize: 12 }} />
                                {userData.role}
                            </Typography>
                            {userData.academic_title && (
                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    <Work sx={{ fontSize: 10 }} />
                                    {userData.academic_title}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* User Contact Details */}
                <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.grey[50] }}>
                    <Stack spacing={1.5}>
                        {userData.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Email sx={{ fontSize: 18, color: theme.palette.primary.dark }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Email</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                        {userData.email}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        {userData.contact_phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Phone sx={{ fontSize: 18, color: theme.palette.primary.dark }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Contact</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                        {userData.contact_phone}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        {userData.nationality && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Flag sx={{ fontSize: 18, color: theme.palette.primary.dark }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Nationality</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                        {userData.nationality}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        {/* {userData.university_name && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <School sx={{ fontSize: 18, color: theme.palette.primary.dark }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">University</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                        {userData.university_name}
                                    </Typography>
                                </Box>
                            </Box>
                        )} */}
                    </Stack>
                </Box>

                {/* Menu Items */}
                <MenuItem 
                    component={Link} 
                    to="/supervisor/profile" 
                    onClick={() => setAnchorEl(null)}
                    sx={{ py: 1.5 }}
                >
                    <ListItemIcon>
                        <Person fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                        primary="Profile Details"
                        secondary="View and edit your profile"
                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                    />
                </MenuItem>

                <Divider />

                <MenuItem 
                    onClick={() => {
                        setAnchorEl(null);
                        handleLogout();
                    }} 
                    sx={{ 
                        py: 1.5,
                        color: theme.palette.error.main,
                        '&:hover': {
                            backgroundColor: theme.palette.error.light + '20',
                        }
                    }}
                >
                    <ListItemIcon>
                        <Logout fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText 
                        primary="Logout"
                        secondary="Sign out of your account"
                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                    />
                </MenuItem>
            </Menu>

            {/* Mobile Drawer */}
            <Drawer
                anchor="left"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                PaperProps={{
                    sx: {
                        width: 280,
                    }
                }}
            >
                {drawerContent}
            </Drawer>

            {/* Notification Detail Dialog */}
            <NotificationDialog />
        </>
    );
};

export default SupervisorBar;